import { useEffect } from 'react';

interface UseClickOutsideProps {
  isOpen: boolean;
  onClose?: (value: boolean) => void;
  contentRef: React.RefObject<HTMLElement | null>;
  shouldCloseOnOutsideClick?: boolean;
  shouldDisableScroll?: boolean;
}

export const useClickOutside = ({
  isOpen,
  onClose,
  contentRef,
  shouldCloseOnOutsideClick,
  shouldDisableScroll,
}: UseClickOutsideProps) => {
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose?.(false);
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (contentRef.current && !contentRef.current.contains(event.target as Node)) {
        onClose?.(false);
      }
    };

    if (isOpen) {
      if (shouldCloseOnOutsideClick) {
        document.addEventListener('keydown', handleEscape);
        document.addEventListener('mousedown', handleClickOutside);
      }

      if (shouldDisableScroll) {
        document.body.classList.add('overflow-y-hidden');
      }
    }

    return () => {
      if (shouldCloseOnOutsideClick) {
        document.removeEventListener('keydown', handleEscape);
        document.removeEventListener('mousedown', handleClickOutside);
      }

      if (shouldDisableScroll) {
        document.body.classList.remove('overflow-y-hidden');
      }
    };
  }, [isOpen, onClose, shouldCloseOnOutsideClick, shouldDisableScroll]);
};
